/**
 * Where everything lives.
 *
 * The marketing site, the dashboard and the API are three origins, and the
 * marketing site links to the other two constantly — every "Get started"
 * button, every widget demonstration, every generator handoff. Collected here
 * so a deployment changes one file, and so no component ever hard-codes
 * `https://app.reviewsjet.com` into a href where nobody can find it later.
 */

function fromEnv(key: string, fallback: string): string {
  const value = import.meta.env[key];
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

export const site = {
  /** This site. Canonical URLs and OpenGraph are absolute against it. */
  url: fromEnv('VITE_SITE_URL', 'https://reviewsjet.com'),
  name: 'ReviewsJet',
  /** The @ handle on the card metadata. */
  twitter: '@ReviewsJetHQ',

  /**
   * The dashboard. A separate application on a separate origin — which is the
   * fact that shapes the whole generator-to-signup handoff, since nothing in
   * `sessionStorage` here can be read there.
   */
  app: fromEnv('VITE_APP_URL', 'https://app.reviewsjet.com'),

  /**
   * The API. Marketing pages read the public widget endpoints from it, server
   * side, to draw real reviews rather than invented ones.
   */
  api: fromEnv('VITE_API_URL', 'https://api.reviewsjet.com'),

  support: 'https://support.reviewsjet.com',
  email: 'hello@reviewsjet.com',
} as const;

export function appUrl(path: string): string {
  return new URL(path, site.app).toString();
}

/**
 * The UTM tags every "Start free" link carries into the dashboard.
 *
 * `utm_content` names the button — the same string as the `surface` the click
 * is tracked under — so a sign-up can be traced to the exact placement that
 * sent it. `utm_source` is this site's own hostname on purpose: the dashboard
 * treats a touch from its own hostnames as internal (`isAttributableTouch`),
 * so these tags describe the click-through without ever replacing the
 * campaign that actually brought the visitor.
 */
export const START_UTM = {
  utm_source: 'reviewsjet.com',
  utm_medium: 'website',
  utm_campaign: 'start_free',
} as const;

/**
 * Where "Start free" sends people in the dashboard.
 *
 * `/signup` for now: a plain account form, then the dashboard's own
 * onboarding. The website-led flow at `/start` still exists and still works —
 * the widget builder's "Use this widget on my website" goes there with its
 * draft (`startUrlWithHandoff`) — and switching every button back to it is
 * changing this one value to `'/start'`.
 */
export const START_FREE_PATH: '/signup' | '/start' = '/signup';

/** "Start free", tagged with where on this site it was opened from. */
export function startUrl(placement: string): string {
  const url = new URL(START_FREE_PATH, site.app);
  for (const [key, value] of Object.entries(START_UTM)) url.searchParams.set(key, value);
  url.searchParams.set('utm_content', placement);
  return url.toString();
}

export function absoluteUrl(path: string): string {
  return new URL(path, site.url).toString();
}

/**
 * ReviewsJet's own workspace, whose published reviews are the social proof on
 * this site.
 *
 * The previous marketing site hard-coded a legacy workspace id into a React
 * component (`<reviewsjet-slider id="65a58aaa8e7e78680643d312">`). That id
 * belongs to the *old* database; the rebuilt API assigns its own, so the value
 * has to be configurable rather than copied across and quietly rendering an
 * empty widget.
 *
 * Left empty by default on purpose. Every surface that shows ReviewsJet's own
 * reviews must handle "not configured" as a real state — an empty social-proof
 * section is a bug worth seeing in development, not a section that silently
 * disappears in production.
 */
export const reviewsJetWorkspaceId = fromEnv('VITE_REVIEWSJET_WORKSPACE_ID', '');

/**
 * Where ReviewsJet's own reviews are read from.
 *
 * Defaults to this deployment's public widget route on `site.api`. Overridable
 * as a full URL because local development usually keeps `VITE_API_URL` on
 * localhost (which has no ReviewsJet workspace) and reads the quotes from
 * production instead: `https://app.reviewsjet.com/api/public/widget/site-reviews/list`.
 */
export function testimonialsEndpoint(): string {
  return fromEnv(
    'VITE_TESTIMONIALS_URL',
    new URL('/api/public/widget/site-reviews/list', site.api).toString()
  );
}
